import React, { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, Save } from "lucide-react";

const DOMAINS = ["Java","Python","AI/ML","Data Analytics","Data Science","MERN","DevOps","Cloud","QA/Testing"];
const EXP = ["Fresher","0-1 Years","1-3 Years","3-5 Years","5+ Years"];

export default function AdminJDGenerator() {
  const [f, setF] = useState({ domain: "Python", experience_category: "1-3 Years", location: "Hyderabad", employment_type: "Full-time", openings: 1, skills: "" });
  const [jd, setJd] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const skills = f.skills.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/jd-generator", { ...f, skills });
      setJd(data);
      toast.success("JD draft generated");
    } catch(e) { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  const publish = async () => {
    if (!jd) return;
    try {
      const skills = f.skills.split(",").map(s => s.trim()).filter(Boolean);
      const payload = {
        title: jd.title,
        department: f.domain === "AI/ML" || f.domain === "Data Analytics" || f.domain === "Data Science" ? "AI & Data" :
                    f.domain === "DevOps" || f.domain === "Cloud" ? "Cloud & DevOps" :
                    f.domain === "QA/Testing" ? "QA & Testing" : "Software Engineering",
        location: f.location, domain: f.domain, experience_category: f.experience_category,
        experience_min: f.experience_category === "Fresher" ? 0 : (f.experience_category === "0-1 Years" ? 0 : parseInt(f.experience_category)),
        experience_max: f.experience_category === "Fresher" ? 1 : (f.experience_category === "0-1 Years" ? 1 : parseInt(f.experience_category)+2),
        employment_type: f.employment_type, openings: Number(f.openings) || 1,
        about: jd.about, responsibilities: jd.responsibilities || [],
        required_qualifications: jd.required_qualifications || [],
        preferred_qualifications: jd.preferred_qualifications || [],
        required_skills: jd.required_skills || skills,
        preferred_skills: jd.preferred_skills || [],
        benefits: jd.benefits || [],
        status: "published", assessment_required: true,
        assessment_duration_min: 20, assessment_questions_count: 10, passing_score: 60
      };
      await api.post("/admin/jobs", payload);
      toast.success("Job published");
      setJd(null);
    } catch(e){ toast.error("Publish failed"); }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">AI-Powered</div>
        <h1 className="font-display text-3xl font-bold text-white mt-1 flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#F59E0B]"/>JD Generator</h1>
        <p className="text-slate-400 mt-1">Generate a professional Job Description draft in seconds.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900 border-slate-800 p-6">
          <h2 className="font-semibold text-white">Inputs</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div><Label className="text-slate-300">Domain</Label>
              <Select value={f.domain} onValueChange={v=>setF({...f, domain:v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="jd-domain"><SelectValue/></SelectTrigger>
                <SelectContent>{DOMAINS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Experience</Label>
              <Select value={f.experience_category} onValueChange={v=>setF({...f, experience_category:v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="jd-exp"><SelectValue/></SelectTrigger>
                <SelectContent>{EXP.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Location</Label>
              <Input value={f.location} onChange={e=>setF({...f, location:e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="jd-location"/>
            </div>
            <div><Label className="text-slate-300">Employment Type</Label>
              <Select value={f.employment_type} onValueChange={v=>setF({...f, employment_type:v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue/></SelectTrigger>
                <SelectContent>{["Full-time","Part-time","Contract","Internship"].map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Openings</Label>
              <Input type="number" value={f.openings} onChange={e=>setF({...f, openings:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/>
            </div>
            <div className="md:col-span-2"><Label className="text-slate-300">Skills (comma separated)</Label>
              <Input value={f.skills} onChange={e=>setF({...f, skills:e.target.value})} placeholder="Python, FastAPI, SQL" className="bg-slate-800 border-slate-700 text-white" data-testid="jd-skills"/>
            </div>
          </div>
          <Button onClick={generate} disabled={loading} className="mt-6 bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold w-full" data-testid="jd-generate">
            <Sparkles className="w-4 h-4 mr-2"/>{loading ? "Generating..." : "Generate JD"}
          </Button>
        </Card>

        <Card className="bg-slate-900 border-slate-800 p-6 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-white">Draft</h2>
            {jd && <Button size="sm" onClick={publish} className="bg-emerald-600 hover:bg-emerald-700" data-testid="jd-publish"><Save className="w-4 h-4 mr-1"/>Publish as Job</Button>}
          </div>
          {!jd ? (
            <p className="text-sm text-slate-500 mt-6">Fill in the inputs and click Generate JD to see a professional draft here.</p>
          ) : (
            <div className="mt-4 space-y-4 text-sm text-slate-300">
              <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Title</Label>
                <Input value={jd.title} onChange={e=>setJd({...jd, title:e.target.value})} className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="jd-preview-title"/>
              </div>
              <div><Label className="text-slate-400 text-xs uppercase tracking-wider">About</Label>
                <Textarea rows={3} value={jd.about} onChange={e=>setJd({...jd, about:e.target.value})} className="bg-slate-800 border-slate-700 text-white mt-1"/>
              </div>
              <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Responsibilities</Label>
                <Textarea rows={5} value={(jd.responsibilities||[]).join("\n")} onChange={e=>setJd({...jd, responsibilities: e.target.value.split("\n").filter(Boolean)})} className="bg-slate-800 border-slate-700 text-white mt-1"/>
              </div>
              <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Required Skills</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(jd.required_skills||[]).map(s=><Badge key={s} className="bg-blue-500/20 text-blue-300 border-none hover:bg-blue-500/20">{s}</Badge>)}
                </div>
              </div>
              <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Preferred Skills</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(jd.preferred_skills||[]).map(s=><Badge key={s} className="bg-slate-700 text-white border-none hover:bg-slate-700">{s}</Badge>)}
                </div>
              </div>
              <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Benefits</Label>
                <ul className="mt-1 list-disc list-inside text-slate-300">
                  {(jd.benefits||[]).map((b,i)=><li key={i}>{b}</li>)}
                </ul>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
